/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';
import classnames from 'classnames';

import './RapidResponseQuestion.scss';
import ExpandableNestedQuestion from './components/ExpandableNestedQuestion';
import QuestionNavControls from './components/QuestionNavControls';
import SurveyHint from './components/SurveyHint';
import Card, { CardSection } from '../../../components/card/Card';

import { eSurveyQuestionCategoryName, iSurveyResponse } from '../../../../apiServices/SurveyService';
import { useUIState } from '../../../hooks/useUIState';
import SurveyQuestionResponseModel from '../../../../models/SurveyQuestionResponseModel';
import { SurveyQuestionProvider, useSurveyQuestion } from '../../../hooks/useSurveyQuestion';

const RapidResponseSingleOption = (props) => {
  const { option, isSelected, children, htmlId } = props;
  const responseId = option.response.id;
  const inputId = `${htmlId}-${option.response.id}`;

  const className = isSelected ? 'theme-group base background font-color-gsLightest' : 'theme-group base border color';

  return (
    <div className={classnames('cmp-rapidresponse-option font-footnote', className)}>
      <input
        id={inputId}
        type="radio"
        name={htmlId}
        value={responseId}
        onChange={() => props.onClick(responseId)}
        disabled={false}
        checked={isSelected}
      />
      <label htmlFor={inputId}>
        {children}
      </label>
    </div>
  );
};

interface RapidResponseSubQuestionProps {
  subQuestion: any;
  answer: any;
  optionIds: number[];
  optionsLookup: any;
  onUpdate(data): void;
  isLast: boolean;
  isWideDesktop: boolean;
  idPrefix: string;
  questionClassSelector: string;
  triggerNewLayout(): void;
  segmentIndex: number;
  subQuestionIndex: number;
}

function getBottomPositionOfHeader() {
  const headerBorder: any = document.querySelector('.cmp-survey-page-layout-sticky-border');
  const hHeight = headerBorder.style.top ? Number(headerBorder.style.top.split('px')[0]) : headerBorder.getBoundingClientRect().bottom;
  return hHeight + 1;
}

const RapidResponseSubQuestion = (props: RapidResponseSubQuestionProps) => {
  const { subQuestion, answer, optionIds, optionsLookup, isLast, isWideDesktop, onUpdate, idPrefix, questionClassSelector, triggerNewLayout, segmentIndex, subQuestionIndex } = props;
  const answersRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const testSizeRef = React.useRef(null);
  const [marginBottom, setMarginBottom] = React.useState(0);
  const [sizeClassName, setSizeClassName] = React.useState('');

  const hasSubResponseAnswer = SurveyQuestionResponseModel.hasResponseForSubQuestion(answer);
  const selectedOption = answer.id ? optionsLookup[answer.id] : null;
  const selectedOptionHasFollowUpQuestion = selectedOption && Boolean(selectedOption.question);

  React.useEffect(() => {
    if (isLast) {
      const hHeight = getBottomPositionOfHeader();
      const extraBuffer = 14;
      const mBottom =  window.innerHeight - hHeight - extraBuffer - cardRef.current.offsetHeight;
      setMarginBottom(mBottom);
    } else {
      setMarginBottom(0);
    }
  }, [isLast]);

  React.useEffect(() => {
    const containerRight = containerRef.current ? containerRef.current.getBoundingClientRect().right - 10 : 0;
    const tempNode = answersRef.current.cloneNode(true);
    testSizeRef.current.appendChild(tempNode);
    const answersRefSize = testSizeRef.current.firstElementChild.lastElementChild.getBoundingClientRect().right;
    testSizeRef.current.removeChild(tempNode);
    setSizeClassName((answersRefSize > containerRight || !isWideDesktop) ? '__column' : '');
    let triggerTimeout = setTimeout(() => {
      triggerNewLayout();
    }, 30);

    return () => {
      clearTimeout(triggerTimeout);
      triggerTimeout = null;
    };
  }, [window.innerWidth, isWideDesktop]);

  return (
    <div ref={cardRef} className={questionClassSelector} data-segment={segmentIndex} data-index={subQuestionIndex} >
    <Card style={{ overflow: 'hidden', marginBottom }}>
      <CardSection hasBorder={selectedOptionHasFollowUpQuestion}>
        <div id={`${idPrefix}${subQuestion.uid}`} ref={containerRef} />
        <div className="font-body">{subQuestion.text}</div>

        <div ref={testSizeRef} />

        <div className={sizeClassName}>
          <div className="cmp-question--rapidresponse" ref={answersRef}>
            {
              optionIds.map((oId, x) => {
                const o = optionsLookup[oId];
                return (
                  <RapidResponseSingleOption
                    key={x}
                    option={o}
                    htmlId={subQuestion.uid}
                    isSelected={answer.id === o.response.id}
                    onClick={(id) => {
                      const newResponse = SurveyQuestionResponseModel.updateSingleRapidResponseAnswer(subQuestion.uid, id, null);
                      onUpdate(newResponse);
                    }}
                  >
                    {o.text}
                  </RapidResponseSingleOption>
                );
              })
            }
          </div>
        </div>
      </CardSection>

      {
        selectedOptionHasFollowUpQuestion && (
          <div className="survey-question-followup">
          <ExpandableNestedQuestion
            defaultExpanded={!hasSubResponseAnswer}
            question={selectedOption.question}
            response={answer}
            valueText={SurveyQuestionResponseModel.getQuestionOptionText(selectedOption.question, answer)}
            onUpdate={(newSubResponse) => {
              const newResponse = SurveyQuestionResponseModel.updateSingleRapidResponseAnswer(subQuestion.uid, answer.id, newSubResponse);
              onUpdate(newResponse);
            }}
          />
          </div>
        )
      }
    </Card>
    </div>
  );
};

interface RapidResponseQuestionProps {
  question: any;
  response: any;
  onUpdate: (data: any, index: number, d: any, o: any) => void;
  hasLastItem: boolean;
  showHint: boolean;
  idPrefix: string;
  questionClassSelector: string;
  segmentIndex: number;
}

const RapidResponseQuestion = (props: RapidResponseQuestionProps) => {
  const { onUpdate, question, response, hasLastItem, showHint, idPrefix, questionClassSelector, segmentIndex } = props;
  const rapidResponseQuestion = question[eSurveyQuestionCategoryName.rapidResponse];
  const rapidResponseAnswerList = _.get(response, `${eSurveyQuestionCategoryName.rapidResponse}.list`, []);
  const [uiState] = useUIState();
  const isWideDesktop = uiState.isWideDesktop;

  const [, surveyQuestionActions] = useSurveyQuestion();

  const { options } = rapidResponseQuestion;
  const responseLookup = _.keyBy(rapidResponseAnswerList, 'uid');
  const optionsLookup = _.keyBy(options, 'response.id');
  const optionIds = options.map(o => o.response.id);

  const subQuestionLastIndex = rapidResponseQuestion.list.length - 1;
  return (
    <div className={`question-segment-${segmentIndex}`}>
      {
        showHint && (
          <SurveyHint>
            <FormattedMessage id="surveyQuestion.rapidResponse.hint" />
          </SurveyHint>
        )
      }

      {
        rapidResponseQuestion.list.map((subQuestion, i) => {
          const liResponse = _.get(responseLookup[subQuestion.uid], 'option', {});
          return (
            <RapidResponseSubQuestion
              key={subQuestion.uid}
              segmentIndex={segmentIndex}
              subQuestionIndex={i}
              subQuestion={subQuestion}
              answer={liResponse}
              idPrefix={idPrefix}
              questionClassSelector={questionClassSelector}
              triggerNewLayout={surveyQuestionActions.triggerNewLayout}
              onUpdate={(d) => {
                surveyQuestionActions.updateRapidResponseSubQuestion(segmentIndex, i, subQuestion, d);

                const selectedOption = optionsLookup[d.option.id];
                const updatedResponse = SurveyQuestionResponseModel.updateRapidResponseAnswer(subQuestion.uid, response, d);
                onUpdate(updatedResponse, i, d, selectedOption);
              }}
              optionIds={optionIds}
              optionsLookup={optionsLookup}
              isLast={i === subQuestionLastIndex && hasLastItem}
              isWideDesktop={isWideDesktop}
            />
          );
        })
      }

    </div>
  );
};

interface RapidResponseAnswerProps {
  className: string;
  style: object;
  answer: iSurveyResponse;
  question: any;
}

export const RapidResponseAnswer = (props: RapidResponseAnswerProps) => {
  const answer = props.answer[eSurveyQuestionCategoryName.rapidResponse];
  const question = props.question[eSurveyQuestionCategoryName.rapidResponse];
  const listLookup = _.keyBy(question.list, 'uid');
  const optionsLookup = _.keyBy(question.options, 'response.id');
  const subOptionsLookup = {};
  question.options.filter((o) => o.question).forEach(o => {
    subOptionsLookup[o.response.id] = {};

    o.question[o.question.category].options.forEach(qo => {
      subOptionsLookup[o.response.id][qo.response.id] = qo.text;
    });
  });

  const answerText = answer.list.map((a, i) => {
    const rrItem = listLookup[a.uid];
    if (!rrItem) { return null; }
    const rrItemText = rrItem.text;
    const answerOption = a.option;
    const rrOptionText = <span key={answerOption.id} className="util-bold">{optionsLookup[answerOption.id].text}</span>;
    const text = [rrItemText, ' — ', rrOptionText];

    if (answerOption.category) {
      const subListOptionId = answerOption[answerOption.category].id;
      const ff = subOptionsLookup[answerOption.id][subListOptionId];
      text.push(' — ');
      text.push(<span key={subListOptionId} className="util-bold">{ff}</span>);
    }
    const isLast = i === answer.list.length - 1;
    return <li key={i} style={{ marginBottom: isLast ? 0 : 18 }}>{text}</li>;
  });

  return (
    <ul className={classnames('cmp-rapidresponse-review', props.className)} style={props.style}>
      {answerText}
    </ul>
  );
};

const RapidResponseQuestionViewRef = (props, ref) => {
  const { onUpdate, question, response, idPrefix, questionClassSelector } = props;
  const questionNavControlRef = React.useRef(null);
  const [uiState] = useUIState();
  const isWideDesktop = uiState.isWideDesktop;

  React.useImperativeHandle(ref, () => ({
    onDisabledClick: () => {
      questionNavControlRef.current.scrollToFirstUncheckedItem(props, isWideDesktop, idPrefix);
    },
    scrollToFirstUncheckedItem: () => {
      questionNavControlRef.current.scrollToFirstUncheckedItem(props, isWideDesktop, idPrefix);
    },
  }));

  function handleOnUpdate(newFullResponse, index, newPartialResponse, selectedOption) {
    onUpdate(newFullResponse);
  }

  return (
    <SurveyQuestionProvider question={question} response={response}>
      <RapidResponseQuestion
        {...props}
        onUpdate={handleOnUpdate}
        segmentIndex={0}
      />
      <QuestionNavControls
        ref={questionNavControlRef}
        question={question}
        response={response}
        idPrefix={idPrefix}
        questionClassSelector={questionClassSelector}
      />
    </SurveyQuestionProvider>
  );
};
export const RapidResponseQuestionView = React.forwardRef(RapidResponseQuestionViewRef);

export default RapidResponseQuestion;
